import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { getDayStartToEndRange } from '@/utils/dateutil';
import { IGetNotificationRes } from '@/types/api/notification/getNotification';
import { IGetCareRecordRes } from '@/types/api/care-record/getCareRecord';
import { IGetResamples, IResample } from '@/types/api/ultra-sonic-data/getResample';
import { IGetSensorReaction, IGetSensorReactionRes } from '@/types/api/ultra-sonic-data/getSensorReaction';
import { IGetUrineTrendAnalysisRes } from '@/types/api/ultra-sonic-data/getUrineTrendAnalysis';
import { IGetActivityStatusAnalysisRes } from '@/types/api/ultra-sonic-data/getActivityStatusAnalysis';
import { UrineGraphSourceData} from "./useFetchUrineGraphData";
import { mathRound } from '@/utils/dataFormatUtil';
import { generateTimeLabelArray } from '@/utils/arrayUtil';
import { color } from '@/styles';
import detectUrinationEvents from '@/utils/detectUrinationEvents';
import { t } from 'i18next';

// dayjs plugins
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

interface HeatmapGraph {
  data: number[][];
  xCategories: string[];
  yCategories: string[];
}

interface HeatmapPlotPoint {
  x: number,
  y: number,
  color: string,
  name: string
}

interface ActivityHeatmapGraphData {
  data: number[][];
  xCategories: string[];
  yCategories: string[];
}

interface OverThresholdGraphData {
  data: number[][];
  threshold: number;
  xCategories: string[];
  yCategories: string[];
}

interface AverageHeatmapGraphData {
  data: number[][];
  xCategories: string[];
  yCategories: string[];
}

interface DetectUrinationGraphData {
  data: Record<string, boolean[]>;
  xCategories: string[];
  yCategories: string[];
}

interface SensorGraph {
  [date: string]: {
    start: Dayjs;
    end: Dayjs;
    sensorData: [number, number | null][];
    resampleData: [number, number | null][];
    postureData: [number, number, number | null][];
  };
}

interface CareRecordItem {
  day: string;
  time: string;
  urinatedInToilet: boolean;
  urinatedInPad: boolean;
}

interface ScatterItem {
  x: number;
  y: number;
  color: string;
  name: string;
}

interface HeatmapEntryBase {
  timestamp: string;
}

interface HeatmapPoint {
  day: string;
  time: string;
  level: number;
}

interface SupportGraphData {
  stackbarData: { name: string; data: number[] }[];
  pieData: { name: string; y: number }[];
  statisticsData: { name: string; data: number }[];
  xCategories: string[];
  careCountData: { name: string; data: string }[];
}

interface ExtractHeatmapOptions<T extends HeatmapEntryBase> {
  getLevel: (entry: T) => number;
  intervalMin: number;
}

export interface UrineReportData {
  sensorReactionArray: SensorGraph;
  careRecordScatter: ScatterItem[];
  careRecordGraphData: SupportGraphData;
  notificationScatter: ScatterItem[];
  heatmapData: HeatmapGraph;
  averageHeatmap: AverageHeatmapGraphData;
  activityHeatmap: ActivityHeatmapGraphData;
  careRecordPlotPoints: HeatmapPlotPoint[];
  notificationPlotPoints: HeatmapPlotPoint[];
  overThresholdHeatmap: OverThresholdGraphData;
  detectUrinationFlags: DetectUrinationGraphData;
  hasGraphData: {
    heatmap: boolean;
    urineGraph: boolean;
    supportGraph: boolean;
  }
}

export const createUrineReport = (
    fetchedData: UrineGraphSourceData,
    threshold: number,
    timeRange: string,
    selectedStartTime: string,
    selectedEndTime: string,
): UrineReportData => {

  // ヒートマップおよびヒートマッププロットの時間間隔(分)
  const intervalMin = 30;
  const activityIntervalMin = 5;

  const careRecordStatisticsKey = [
    t('ToiletUrinationSuccessRate', 'トイレ排尿成功率'),
    t('ToiletUrinationRate', 'トイレ排尿率'),
    t('diaperUrinationRate', 'おむつ排尿率'),
    t('MissedToiletUrinationRate', '空振り率'),
  ];

  const careRecordCountKey = [
    t('TotalNumberOfCareInterventions', 'ケア介入合計回数'),
    t('AverageNumberOfCareInterventions', 'ケア介入平均回数'),
    t('contaminationOfClothesAndSheetsCount', '衣類・シーツ汚染回数'),
  ];
  const convertTimestamp = (timestamp: string | number, intervalMin: number): undefined | {minute: number, formattedTime: string, day: string} => {
    let currentTime = dayjs(timestamp);
    let [startOfDay, endOfDay] = getTimeRange(currentTime);

    if (!currentTime.isBetween(startOfDay, endOfDay, null, '[]')) {
      [startOfDay, endOfDay] = getTimeRange(currentTime.subtract(1, 'day'));
      if (!currentTime.isBetween(startOfDay, endOfDay, null, '[]')) return;
    }

    const minute = Math.floor(currentTime.minute() / intervalMin) * intervalMin;
    const formattedTime = `${currentTime.hour()}:${String(minute).padStart(2, '0')}`;
    const day = startOfDay.local().format('YYYY/MM/DD');

    return {minute, formattedTime, day}
  }

  // 組み合わせのキーを生成する関数
  const createCareRecordKey = (urinatedInToilet: boolean, urinatedInPad: boolean) =>
      // テキスト変更、対応言語が増える場合はkeyとtextを分離させる必要がある
      // why: createSuccessUrinations内でtextを直接判定条件に使用している
      `${t('toilet', 'トイレ')}: ${urinatedInToilet ? t('yes', '有') : t('no', '無')} ${t('diaper', 'おむつ/パッド')}: ${
          urinatedInPad ? t('yes', '有') : t('no', '無')
      }`;

  // 指定された日時の指定の開始時間から終了時間の範囲を返す
  const getTimeRange = (time: Dayjs) => {
    switch (timeRange) {
      case 'all-time': {
        // 今日の0時から翌日0時までの範囲を返す
        return getDayStartToEndRange(time, 0, 0);
      }
      case 'day-time': {
        // 今日の7時から19時までの範囲を返す
        const startHour = 7;
        const endHour = 19;
        return getDayStartToEndRange(time, startHour, endHour);
      }
      case 'night-time': {
        // 今日の19時から明日の7時までの範囲を返す
        const startHour = 19;
        const endHour = 7;
        return getDayStartToEndRange(time, startHour, endHour);
      }
      case 'selected-time': {
        // 自由設定時間を返す
        const startHour = +selectedStartTime.split(':')[0];
        const endHour = +selectedEndTime.split(':')[0];
        return getDayStartToEndRange(time, startHour, endHour);
      }
      default:
        throw new Error('Invalid selected timeRange');
    }
  };

  // heatmap x軸のCategoriesを生成
  const setXAxisCategories = (intervalMin: number) => {
    let start = '7:00';
    let arrayLength = 12 * (60 / intervalMin);

    switch (timeRange) {
      case 'all-time': {
        start = '0:00';
        arrayLength = 24 * (60 / intervalMin);
        break;
      }
      case 'day-time': {
        start = '7:00';
        arrayLength = 12 * (60 / intervalMin);
        break;
      }
      case 'night-time': {
        start = '19:00';
        arrayLength = 12 * (60 / intervalMin);
        break;
      }
      case 'selected-time': {
        start = selectedStartTime;
        const startHour = +selectedStartTime.split(':')[0];
        const endHour = +selectedEndTime.split(':')[0];
        // startとendが同じ場合は24時間扱い
        arrayLength =
          startHour < endHour
            ? (endHour - startHour) * (60 / intervalMin)
            : (24 - (startHour - endHour)) * (60 / intervalMin);
        break;
      }
      default:
        throw new Error('Invalid selected timeRange');
    }
    const xCategories = generateTimeLabelArray(start, intervalMin, arrayLength);
    return xCategories;
  };

  const extractHeatmap = <T extends HeatmapEntryBase>(
      data: T[],
      options: ExtractHeatmapOptions<T>
  ) => {
    const { getLevel, intervalMin } = options;
    const dateSet = new Set<string>();
    const heatmapData: HeatmapPoint[] = [];
    const xCategories = setXAxisCategories(intervalMin);

    data.forEach((entry) => {
      const timestamps = convertTimestamp(entry.timestamp, intervalMin);
      if (!timestamps) return;
      const { formattedTime, day} = timestamps;
      dateSet.add(day);

      heatmapData.push({
        day,
        time: formattedTime,
        level: getLevel(entry),
      });
    });

    const yCategories = Array.from(dateSet).sort();
    const formattedData = heatmapData.map((r) => [
      xCategories.indexOf(r.time),
      yCategories.indexOf(r.day),
      r.level,
    ]);

    return {
      data: formattedData,
      xCategories,
      yCategories,
    };
  };

  // 日別Heatmap生成
  const createHeatmapData = (data: IGetUrineTrendAnalysisRes): HeatmapGraph => {
    return extractHeatmap(data, {
      getLevel: (entry) => entry.urineLevel,
      intervalMin,
    });
  };

  const createActivityHeatmapData = (data: IGetActivityStatusAnalysisRes): ActivityHeatmapGraphData => {
    // activityStatusが2（活動状態の時）は他の姿勢より優先して活動状態であることを示す
    // 活動状態は姿勢で定義されていない番号を使う
    // 7: 活動状態かつ寝ている状態 8: 活動状態かつ立っている状態
    return extractHeatmap(data, {
      getLevel: (entry) => (entry.activityStatus === 2 ? ([0, 1, 2].includes(entry.posture) ? 8 : 7) : entry.posture),
      intervalMin: activityIntervalMin,
    });
  };

  const createSupportGraphData = (data: IGetCareRecordRes[]): SupportGraphData => {
    const plotData: CareRecordItem[] = [];

    const filteredData = data.filter((r) => r.urinatedToilet !== null);
    filteredData.forEach((r) => {
      const timestamps = convertTimestamp(r.timestamp, intervalMin);
      if (!timestamps) return;
      const { formattedTime, day} = timestamps;

      const urinatedInToilet = !!r.urinatedToilet;
      const urinatedInPad = ['large', 'medium', 'small'].includes(r.padUrineVolumeType || '');

      plotData.push({
        day,
        time: formattedTime,
        urinatedInToilet,
        urinatedInPad,
      });
    });

    if (!plotData || !plotData.length)
      return { stackbarData: [], pieData: [], statisticsData: [], xCategories: [], careCountData: [] };

    const groupedData = plotData.reduce((acc: any, item: CareRecordItem) => {
      const day = item.day;
      const key = createCareRecordKey(item.urinatedInToilet, item.urinatedInPad);

      if (!acc[day]) {
        acc[day] = {
          [createCareRecordKey(true, false)]: 0,
          [createCareRecordKey(true, true)]: 0,
          [createCareRecordKey(false, true)]: 0,
          [createCareRecordKey(false, false)]: 0,
        };
      }
      if (!acc[day][key]) {
        acc[day][key] = 0;
      }
      acc[day][key]++;

      return acc;
    }, {});

    const xCategories = Object.keys(groupedData);
    const stackbarData = Object.keys(groupedData[Object.keys(groupedData)[0]]).map((key) => ({
      name: key,
      data: Object.values(groupedData).map((item: any) => item[key] || 0),
    }));

    const totalSum = stackbarData.reduce((total, item) => total + item.data.reduce((sum, value) => sum + value, 0), 0);
    const pieData = stackbarData.map((item) => ({
      name: item.name,
      y: mathRound((item.data.reduce((sum, value) => sum + value, 0) / totalSum) * 100, 0),
    }));

    const statisticsData = careRecordStatisticsKey.map((key, index) => {
      const value = index === 1 ? pieData[0]?.y + pieData[1]?.y : pieData[index]?.y;
      return {
        name: key,
        data: value,
      };
    });

    const careCountData = careRecordCountKey.map((key, index) => {
      const totalCount = stackbarData.reduce((sum, v) => sum + v.data.reduce((acc, num) => acc + num, 0), 0);
      const leakCount = data.reduce((sum, v) => sum + (v.isLeakOutUrine ? 1 : 0), 0);

      const value =
          index === 0
              ? `${totalCount}${t('Times', '回')}`
              : index === 1
                  ? `${mathRound(totalCount / stackbarData[0].data.length, 1)}${t('TimesPerDay', '回 / 日')}`
                  : `${leakCount}${t('Times', '回')}`;
      return {
        name: key,
        data: value,
      };
    });

    return { stackbarData, pieData, statisticsData, xCategories, careCountData };
  };

  const createHeatmapPlot = (data: ScatterItem[], xCategories: string[], yCategories: string[]
  ): HeatmapPlotPoint[] => {
    const plotData: { day: string; time: string; color: string; name: string }[] = [];

    data.forEach((r) => {
      const timestamps = convertTimestamp(r.x, intervalMin);
      if (!timestamps) return;
      const { formattedTime, day} = timestamps;

      plotData.push({
        day,
        time: formattedTime,
        color: r.color,
        name: r.name,
      });
    });

    const formattedData = plotData.map((r) => {
      const x = xCategories.indexOf(r.time);
      const y = yCategories.indexOf(r.day);
      return { x, y, color: r.color, name: r.name };
    });

    return formattedData;
  };

  // 通知プロット生成
  const createNotificationScatterData = (data: IGetNotificationRes[]) => {
    const scatterData: ScatterItem[] = [];

    data.forEach((r) => {
      const x = dayjs(r.timestamp).valueOf();
      const y = 0; //通知プロットはグラフの一番下に描画する
      const colors =
          r.notificationType === 'urinated'
              ? color.notification_pink
              : r.notificationType === 'bigBladder'
                  ? color.notification_yellow
                  : r.notificationType === 'gotUp'
                      ? color.notification_red
                      : null;
      const name =
          r.notificationType === 'urinated'
              ? t('checkGraphLabel', 'でたかも')
              : r.notificationType === 'bigBladder'
                  ? t('almostGraphLabel', 'そろそろ')
                  : r.notificationType === 'gotUp'
                      ? t('gotUpGraphLabel', '起き上がり')
                      : null;

      if (colors && name) {
        scatterData.push({ x, y, color: colors, name });
      }
    });
    return scatterData;
  };

  // ケア履歴プロット生成
  const createCareRecordScatter = (data: IGetCareRecordRes[]) => {
    const scatterData: ScatterItem[] = [];

    const filteredData = data.filter((r) => r.urinatedToilet !== null);
    filteredData.forEach((r) => {
      const x = dayjs(r.timestamp).valueOf();
      const y = 10; //通知プロットはグラフの一番下に描画する
      const urinatedInToilet = !!r.urinatedToilet;
      const urinatedInPad = ['large', 'medium', 'small'].includes(r.padUrineVolumeType || '');

      const color = urinatedInToilet ? (urinatedInPad ? '#75f94c' : '#72fbfd') : urinatedInPad ? '#CCCCCC' : '#6D6D6D';
      const name = createCareRecordKey(urinatedInToilet, urinatedInPad);

      if (color && name) {
        scatterData.push({ x, y, color, name });
      }
    });
    return scatterData;
  };
  // 日別尿量グラフと補正前グラフ生成
  const createSensorReactionGraphData = (
      sensorReactions: IGetSensorReactionRes,
      sensorResamples: IGetResamples,
      activityStatus: IGetActivityStatusAnalysisRes
  ) => {
    let prevTime: string;

    // データを日付でグループ化
    const sensorData: SensorGraph = {};

    const processData = (array: IGetSensorReactionRes | IGetResamples, dataType: 'sensorData' | 'resampleData') => {
      array.forEach((r: IGetSensorReaction | IResample | any) => {
        const currentTime = dayjs(r.timestamp);
        let [startOfDay, endOfDay] = getTimeRange(dayjs(currentTime));

        if (prevTime) {
          const diffInMin = currentTime.diff(dayjs(prevTime), 'minute');
          // 前のタイムスタンプから25分以上離れていたらnullを挿入
          if (diffInMin >= 25) {
            const newTimestamp = dayjs(prevTime).add(1, 'second');
            const newDate = newTimestamp.format('YYYY/MM/DD');
            if (sensorData[newDate] && sensorData[newDate][dataType].length) {
              sensorData[newDate][dataType].push([newTimestamp.valueOf(), null]);
            }
          }
        }

        prevTime = r.timestamp;

        // 1日ごとにレコードを区切る処理
        // データがstartOfDay以上〜endOfDay未満の間でない場合、スキップ
        if (!currentTime.isBetween(startOfDay, endOfDay, null, '[)')) {
          // 一致しない場合、1日前の日付を基にgetTimeを再度呼び出す
          [startOfDay, endOfDay] = getTimeRange(dayjs(currentTime.subtract(1, 'day')));
          if (!currentTime.isBetween(startOfDay, endOfDay, null, '[)')) return; // この場合も範囲外ならスキップ
        }
        const day = startOfDay.local().format('YYYY/MM/DD');

        if (!sensorData[day]) {
          sensorData[day] = { start: startOfDay, end: endOfDay, sensorData: [], resampleData: [], postureData: [] };
        }

        const value = dataType === 'sensorData' ? r.u1xActiveRatio * 10 : r.semiSensorSummary * 10;
        sensorData[day][dataType].push([dayjs(r.timestamp).valueOf(), value]);
      });
    };

    const processPostureData = (array: IGetActivityStatusAnalysisRes) => {
      array.forEach((r) => {
        const timestamps = convertTimestamp(r.timestamp, activityIntervalMin);
        if (!timestamps) return;

        const { day } = timestamps;

        // sensorDataまたはresampleDataが存在する日付に対してのみpostureDataを追加
        if (sensorData[day]) {
          let posCode;
          // activityStatusが2（活動状態の時）は他の姿勢より優先して活動状態であることを示す
          if (r.activityStatus === 2) {
            // 活動状態は姿勢で定義されていない番号を使う
            // 7: 活動状態かつ寝ている状態 8: 活動状態かつ立っている状態
            posCode = [0, 1, 2].includes(r.posture) ? 8 : 7;
          } else {
            posCode = r.posture;
          }
          sensorData[day].postureData.push([dayjs(r.timestamp).valueOf(), 0, posCode]);
        }
      });
    };

    processData(sensorReactions, 'sensorData');
    processData(sensorResamples, 'resampleData');
    processPostureData(activityStatus);

    // sensorData配列とresampleData配列の両方がない日のレコードは除去する
    const filteredSensorData = Object.fromEntries(
        Object.entries(sensorData).filter(([, entries]) => entries.sensorData.length || entries.resampleData.length)
    );

    // 日付の降順になるようにソート処理を追加
    const sortedSensorData: SensorGraph = Object.fromEntries(
        Object.entries(filteredSensorData).sort((a, b) => dayjs(b[0]).unix() - dayjs(a[0]).unix())
    );
    return sortedSensorData;
  };

  // 日別尿量折線グラフ生成
  const sensorReactionArray = createSensorReactionGraphData(fetchedData.sensorReactions, fetchedData.sensorResamples, fetchedData.activityStatus);

  // 日別尿量グラフ用ケア履歴データ生成
  const careRecordScatter = createCareRecordScatter(fetchedData.careRecords);

  // 誘導回数3種類グラフデータ + 統計情報 生成
  const careRecordGraphData = createSupportGraphData(fetchedData.careRecords);

  // 日別尿量グラフ用通知データ生成
  const notificationScatter = createNotificationScatterData(fetchedData.notifications);

  // 排尿傾向分析heatmap生成
  const heatmapData = createHeatmapData(fetchedData.urineTrend);

  const activityHeatmap = createActivityHeatmapData(fetchedData.activityStatus);

  // heatmap用ケア履歴データ生成
  const careRecordPlotPoints = createHeatmapPlot(careRecordScatter, heatmapData.xCategories, heatmapData.yCategories);

  // heatmap用通知データ生成
  const notificationPlotPoints = createHeatmapPlot(notificationScatter, heatmapData.xCategories, heatmapData.yCategories);

  // 統計量heatmap生成
  const overThresholdHeatmap = createOverThresholdHeatmap(heatmapData, threshold);
  const averageHeatmap = createAverageHeatmap(heatmapData);
  const detectUrinationFlags = createDetectUrinationHeatmap(
      heatmapData,
      overThresholdHeatmap,
      averageHeatmap,
      careRecordPlotPoints
  );

  return {
    sensorReactionArray,
    careRecordScatter,
    careRecordGraphData,
    notificationScatter,
    heatmapData,
    averageHeatmap,
    activityHeatmap,
    careRecordPlotPoints,
    notificationPlotPoints,
    detectUrinationFlags,
    overThresholdHeatmap,
    hasGraphData: {
      heatmap: !!heatmapData.data.length,
      urineGraph: !!Object.entries(sensorReactionArray).length,
      supportGraph: !!careRecordGraphData.stackbarData.length,
    }
  }
}

export const updateWithThreshold = (threshold: number, urineReportData: UrineReportData) => {
  if (urineReportData) {
    const updatedOverThresholdHeatmap = createOverThresholdHeatmap(urineReportData.heatmapData, threshold);
    const updatedDetectUrinationFlags = createDetectUrinationHeatmap(
        urineReportData.heatmapData,
        updatedOverThresholdHeatmap,
        urineReportData.averageHeatmap,
        urineReportData.careRecordPlotPoints
    );

    return {updatedOverThresholdHeatmap, updatedDetectUrinationFlags};

  }
}

const createOverThresholdHeatmap = (heatmapData: HeatmapGraph, threshold: number): OverThresholdGraphData => {
  if (!heatmapData?.data?.length || !threshold) {
    return {
      data: [[], [], []],
      threshold: +threshold,
      xCategories: [],
      yCategories: [],
    };
  }

  const counts = new Map<number, number>();
  const countOverThreshold = new Map<number, number>();
  const overThresholdHeatmapData: number[][] = [];

  heatmapData.data.forEach(([x, y, value]) => {
    counts.set(x, (counts.get(x) || 0) + 1);
    if (value >= threshold) {
      countOverThreshold.set(x, (countOverThreshold.get(x) || 0) + 1);
    }
  });

  for (let x = 0; x < heatmapData.xCategories.length; x++) {
    const count = counts.get(x) || 0;
    const overThresholdCount = countOverThreshold.get(x) || 0;
    const percentage = count !== 0 ? Math.round((overThresholdCount * 100) / count) : 0;
    overThresholdHeatmapData.push([x, 0, percentage]);
  }
  return {
    data: overThresholdHeatmapData,
    threshold: +threshold,
    xCategories: heatmapData.xCategories,
    yCategories: heatmapData.yCategories,
  };
};

const createdDataCounts = (heatmapData: HeatmapGraph): number[] => {
  const counts = new Map<number, number>();
  const dataCounts: number[] = [];

  heatmapData.data.forEach(([x, y, value]) => {
    counts.set(x, (counts.get(x) || 0) + 1);
  });

  for (let x = 0; x < heatmapData.xCategories.length; x++) {
    const count = counts.get(x) || 0;
    dataCounts.push(count);
  }

  return dataCounts;
};

const createSuccessUrinations = (heatmapData: HeatmapGraph, careRecordPlot: ScatterItem[]) => {
  const successFlags = new Map<number, number>();
  const successUrinations: number[] = [];

  careRecordPlot.forEach((item) => {
    if (item.name == "トイレ: 有 おむつ: 無" || item.name == "Toilet: Yes Diaper: No") {
      successFlags.set(item.x, 1);
    }
  });

  for (let x = 0; x < heatmapData.xCategories.length; x++) {
    const count = successFlags.get(x) || 0;

    successUrinations.push(count);
  }

  return successUrinations;
};

const createDetectUrinationHeatmap = (
    heatmapData: HeatmapGraph,
    overThresholdHeatmap: OverThresholdGraphData,
    averageHeatmap: AverageHeatmapGraphData,
    careRecordPlot: ScatterItem[]
): DetectUrinationGraphData => {
  if (!overThresholdHeatmap?.data?.length || !averageHeatmap?.data?.length) {
    return {
      data: {},
      xCategories: [],
      yCategories: [],
    };
  }

  const percents = overThresholdHeatmap.data.map((item) => item[2]);
  const means = averageHeatmap.data.map((item) => item[2]);
  const dataCounts = createdDataCounts(heatmapData);
  const successUrinations = createSuccessUrinations(heatmapData, careRecordPlot);

  const flags = detectUrinationEvents(percents, means, successUrinations, dataCounts);
  return {
    data: flags,
    xCategories: overThresholdHeatmap.xCategories,
    yCategories: overThresholdHeatmap.yCategories,
  };
};

const createAverageHeatmap = (heatmapData: HeatmapGraph): AverageHeatmapGraphData => {
  if (!heatmapData?.data?.length) {
    return {
      data: [[], [], []],
      xCategories: [],
      yCategories: [],
    };
  }

  const counts = new Map<number, number>();
  const sums = new Map<number, number>();
  const averageHeatmapData: number[][] = [];

  heatmapData.data.forEach(([x, , value]) => {
    counts.set(x, (counts.get(x) || 0) + 1);
    sums.set(x, (sums.get(x) || 0) + value);
  });

  for (let x = 0; x < heatmapData.xCategories.length; x++) {
    const count = counts.get(x) || 0;
    const sum = sums.get(x) || 0;
    const average = count !== 0 ? Math.round((sum / count) * 10) / 10 : 0;
    averageHeatmapData.push([x, 0, average]);
  }
  return {
    data: averageHeatmapData,
    xCategories: heatmapData.xCategories,
    yCategories: heatmapData.yCategories,
  };
};
